"use client";

import { AnimatePresence, motion } from "framer-motion";
import {
  Apple,
  Beef,
  ChevronDown,
  ChevronRight,
  Fish,
  Leaf,
  Milk,
  Package,
  ShoppingBasket,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { memo, useState } from "react";

import { EditIngredientModal } from "@/components/ingredients/EditIngredientModal";
import { SwipeableIngredientCard } from "@/components/ingredients/SwipeableIngredientCard";
import { Badge } from "@/components/ui/badge";
import { easeLayout, fadeSlideUpVariants, listItemExitVariants } from "@/lib/animations";
import { customCategoryLabel, isBuiltInCategory } from "@/lib/categories";
import type { Category, Ingredient } from "@/lib/types";

interface CategorySectionProps {
  /** A built-in category, or any other one (an agent's own) shown under its own name. */
  category: string;
  ingredients: Ingredient[];
  onToggleIngredient: (id: string) => void;
  onDeleteIngredient: (id: string) => void;
  onUpdateIngredient: (id: string, newText: string) => void;
  onIncrementQuantity?: (id: string) => void;
  onDecrementQuantity?: (id: string) => void;
}

const categoryConfig: Record<Category, { icon: any; badgeColor: string; iconColor: string }> = {
  meat: {
    icon: Beef,
    badgeColor: "bg-category-meat-bg text-category-meat-text",
    iconColor: "text-category-meat-text",
  },
  seafood: {
    icon: Fish,
    badgeColor: "bg-category-seafood-bg text-category-seafood-text",
    iconColor: "text-category-seafood-text",
  },
  produce: {
    icon: Leaf,
    badgeColor: "bg-category-produce-bg text-category-produce-text",
    iconColor: "text-category-produce-text",
  },
  fruit: {
    icon: Apple,
    badgeColor: "bg-category-fruit-bg text-category-fruit-text",
    iconColor: "text-category-fruit-text",
  },
  dairy: {
    icon: Milk,
    badgeColor: "bg-category-dairy-bg text-category-dairy-text",
    iconColor: "text-category-dairy-text",
  },
  other: {
    icon: Package,
    badgeColor: "bg-category-other-bg text-category-other-text",
    iconColor: "text-category-other-text",
  },
};

/** A category the app doesn't know: Other's colors with a basket icon. */
const customCategoryConfig = {
  icon: ShoppingBasket,
  badgeColor: "bg-category-other-bg text-category-other-text",
  iconColor: "text-category-other-text",
};

export const CategorySection = memo(function CategorySection({
  category,
  ingredients,
  onToggleIngredient,
  onDeleteIngredient,
  onUpdateIngredient,
  onIncrementQuantity,
  onDecrementQuantity,
}: CategorySectionProps) {
  const t = useTranslations("categories");
  const [isExpanded, setIsExpanded] = useState(true);
  const [editingIngredientId, setEditingIngredientId] = useState<string | null>(null);
  const [editingIngredientText, setEditingIngredientText] = useState("");
  const [editModalIngredientId, setEditModalIngredientId] = useState<string | null>(null);

  const config = isBuiltInCategory(category) ? categoryConfig[category] : customCategoryConfig;
  const label = isBuiltInCategory(category) ? t(category) : customCategoryLabel(category);
  const Icon = config.icon;

  if (ingredients.length === 0) return null;

  // Find the current ingredient from the ingredients array by ID
  // This ensures we always have the latest ingredient data even after increment/decrement
  const editModalIngredient = editModalIngredientId
    ? ingredients.find((ing) => ing.id === editModalIngredientId) || null
    : null;

  const handleStartEdit = (ingredient: Ingredient) => {
    setEditingIngredientId(ingredient.id);
    const fullText = ingredient.quantity
      ? `${ingredient.quantity} ${ingredient.text}`
      : ingredient.text;
    setEditingIngredientText(fullText);
  };

  const handleSaveEdit = () => {
    if (editingIngredientId && editingIngredientText.trim()) {
      onUpdateIngredient(editingIngredientId, editingIngredientText);
      setEditingIngredientId(null);
      setEditingIngredientText("");
    }
  };

  const handleCancelEdit = () => {
    setEditingIngredientId(null);
    setEditingIngredientText("");
  };

  const handleOpenEditModal = (ingredient: Ingredient) => {
    setEditModalIngredientId(ingredient.id);
  };

  const handleCloseEditModal = () => {
    setEditModalIngredientId(null);
  };

  const handleModalSave = (newText: string) => {
    if (editModalIngredient) {
      onUpdateIngredient(editModalIngredient.id, newText);
    }
  };

  const handleModalArchive = () => {
    if (editModalIngredient) {
      onToggleIngredient(editModalIngredient.id);
    }
  };

  const handleModalDelete = () => {
    if (editModalIngredient) {
      onDeleteIngredient(editModalIngredient.id);
    }
  };

  return (
    <>
      {/* Category Header - stagger item */}
      <motion.div className="top-0 z-50 bg-background mb-2" variants={fadeSlideUpVariants}>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center gap-3 py-2 px-2 rounded-md hover:bg-[var(--elevate-1)] transition-colors cursor-pointer"
          aria-label={isExpanded ? t("collapseCategory") : t("expandCategory")}
          title={isExpanded ? t("collapseCategory") : t("expandCategory")}
        >
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          )}
          <Icon className={`w-5 h-5 ${config.iconColor}`} />
          <span className="font-semibold text-lg">{label}</span>
          <Badge variant="secondary" className={config.badgeColor}>
            {ingredients.length}
          </Badge>
        </button>
      </motion.div>

      {/* Each ingredient card as separate stagger items */}
      {isExpanded && (
        <div className="space-y-2 mb-6">
          <AnimatePresence mode="popLayout">
            {ingredients.map((ingredient, _ingredientIndex) => (
              <motion.div
                key={ingredient.id}
                layout
                variants={fadeSlideUpVariants}
                exit={{
                  ...listItemExitVariants.exit,
                }}
                transition={{
                  layout: easeLayout,
                }}
              >
                <SwipeableIngredientCard
                  ingredient={ingredient}
                  isEditing={editingIngredientId === ingredient.id}
                  editText={editingIngredientText}
                  onToggle={() => onToggleIngredient(ingredient.id)}
                  onDelete={() => onDeleteIngredient(ingredient.id)}
                  onStartEdit={() => handleStartEdit(ingredient)}
                  onSaveEdit={handleSaveEdit}
                  onCancelEdit={handleCancelEdit}
                  onEditTextChange={setEditingIngredientText}
                  onIncrementQuantity={
                    onIncrementQuantity ? () => onIncrementQuantity(ingredient.id) : undefined
                  }
                  onDecrementQuantity={
                    onDecrementQuantity ? () => onDecrementQuantity(ingredient.id) : undefined
                  }
                  onMoreActionsClick={() => handleOpenEditModal(ingredient)}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Edit Ingredient Modal */}
      {editModalIngredient && (
        <EditIngredientModal
          isOpen={!!editModalIngredient}
          onClose={handleCloseEditModal}
          ingredient={editModalIngredient}
          onSave={handleModalSave}
          onArchive={handleModalArchive}
          onDelete={handleModalDelete}
          onIncrementQuantity={
            onIncrementQuantity ? () => onIncrementQuantity(editModalIngredient.id) : undefined
          }
          onDecrementQuantity={
            onDecrementQuantity ? () => onDecrementQuantity(editModalIngredient.id) : undefined
          }
        />
      )}
    </>
  );
});
